import React, { useState, useEffect, useRef, Suspense } from 'react';
import { lazyWithReload as lazy } from './utils/lazyWithReload';
import { validationPlaygroundGate } from './config/validationPlaygroundFlag';
import { realityCheckDemoGate } from './config/realityCheckDemoFlag';
import { pineImporterGate } from './config/pineImporterFlag';
import { ensurePineRemoteConfig, getPineRemoteKillState } from './config/pineRemoteKill';
import { ensureFundamentalsFlag, loadFundamentalsFlag } from './services/fundamentalsFlagService';
import { hypothesisPlaygroundGate } from './config/hypothesisPlaygroundFlag';
import { resolveDiscoveryAccess } from './config/discoveryActivation';
import { logger } from './utils/logger';
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import StrategyBuilderPage from './pages/StrategyBuilderPage';
// AuthModal is no longer imported here — M-E moved the unauth entry
// point to LandingPage, which owns its own AuthModal trigger.
import { useAuth } from './contexts/AuthContext';
import { useIsMobile } from './hooks/useIsMobile';
import { useBuilderMobileOverride } from './hooks/useBuilderMobileOverride';
// BacktestPage pulls in recharts (~300 KB) which the Strategy Builder
// doesn't need. Lazy-load so the initial bundle skips it until a user
// actually clicks the Backtest tab.
const BacktestPage = lazy(() => import('./pages/BacktestPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const OptimizationPanel = lazy(() => import('./components/Optimization/AdaptiveOptimizerPanel'));
const PortfoliosPage = lazy(() => import('./pages/PortfoliosPage'));
const ReplayPage = lazy(() => import('./pages/ReplayPage'));
const DeployPage = lazy(() => import('./pages/DeployPage'));
const SignalsPage = lazy(() => import('./pages/SignalsPage'));
const ConvertPage = lazy(() => import('./pages/ConvertPage'));
// S16 (2026-06-04) — top-bar Dashboard + Community buttons get
// destinations. Stubs first; S17-S20 fill in the real surfaces.
const AdminPage = lazy(() => import('./pages/AdminPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const OnboardingFlow = lazy(() => import('./components/Onboarding/OnboardingFlow'));
// Validation Playground — flag-gated, no-auth review surface (lazy chunk; only
// fetched when #validation-playground is opened with the playground flag on).
const ValidationPlayground = lazy(() => import('./components/Validation/playground/ValidationPlayground'));
// Hypothesis Engine playground — flag-gated, no-auth review surface for the
// teaching layer (Failure Axis gallery). Lazy chunk; fetched only when opened.
const HypothesisPlayground = lazy(() => import('./components/Hypothesis/playground/HypothesisPlayground'));
// Reality Check demo — flag-gated, no-auth, no-DB investor/customer walkthrough on bundled
// real candles. Lazy chunk; fetched only when #reality-check-demo is opened with the flag on.
const RealityCheckDemoPage = lazy(() => import('./components/RealityCheck/RealityCheckDemoPage'));
// Pine → Reality Check demo — the acquisition funnel: paste Pine → import → validate → verdict →
// PDF. Behind the EXISTING Pine importer gate + remote-kill. Lazy chunk; fetched only when opened.
const PineDemoPage = lazy(() => import('./components/RealityCheck/PineDemoPage'));
// Discovery workspace — flag-gated, lazy chunk; only fetched when the Discovery
// tab is opened with the flag ON, so disabled users never pay the Discovery
// runtime bundle cost.
const DiscoveryPage = lazy(() => import('./pages/DiscoveryPage'));
const LandingPage = lazy(() => import('./components/Landing/LandingPage'));
// Pre-auth customer surfaces (M2/M3): assessment status via unguessable token, and a
// tokenized shareable Reality Check report page. Lazy chunks; fetched only when opened.
const AssessmentStatusPage = lazy(() => import('./components/Assessment/AssessmentStatusPage'));
const SharedReportPage = lazy(() => import('./components/RealityCheck/SharedReportPage'));
// Reality Check v1-vs-v2 comparison — experimental R&D surface (versioned, side-by-side; v1 untouched).
// Evidence hub — pre-auth index of every published proof surface (v1 pack, M5, v2 comparison, Mode-B).
const EvidencePage = lazy(() => import('./components/Evidence/EvidencePage'));
const EvidenceArchivePage = lazy(() => import('./components/Evidence/EvidenceArchivePage'));
const ShowcasePage = lazy(() => import('./components/Evidence/ShowcasePage'));
const PageLoading = () => (
  <div className="flex items-center justify-center h-full bg-gray-50">
    <div className="text-gray-500 text-sm">Loading…</div>
  </div>
);

// Unauthenticated landing experience.
//
// M-E replaced the previous minimal AuthGate (logo + auto-open AuthModal)
// with a real marketing landing page. LandingPage owns its own AuthModal
// trigger — users open sign-in / sign-up explicitly via CTA. The auth
// modal is dismissible from the landing, so the page is reachable to
// skeptical visitors who want to read first.
//
// Lazy-loaded so the ~30 KB of landing copy doesn't sit in the
// authenticated-user bundle.

const AuthLoading: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center">
    <div className="text-slate-400 text-sm">Loading…</div>
  </div>
);

// Import error handling components
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import ErrorOverlay from './components/ErrorOverlay';
import { errorReporter, enableErrorDebugging } from './utils/errorHandlers';

// Feature-specific error boundary wrapper
const FeatureErrorBoundary: React.FC<{
  children: React.ReactNode;
  featureName: string;
  fallback?: React.ReactNode;
}> = ({ children }) => {
  return <GlobalErrorBoundary>{children}</GlobalErrorBoundary>;
};

// Page wrapper with error boundary
const PageWrapper: React.FC<{
  children: React.ReactNode;
  pageName: string;
}> = ({ children, pageName }) => {
  return (
    <FeatureErrorBoundary
      featureName={pageName}
      fallback={
        <div className="flex items-center justify-center h-full bg-gray-50">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md">
            <div className="flex items-center mb-4">
              <svg
                className="w-8 h-8 text-red-500 mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <h2 className="text-xl font-bold text-gray-800">
                {pageName} Error
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              There was an error loading the {pageName.toLowerCase()} page.
              Please try refreshing or contact support if the problem persists.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      }
    >
      {children}
    </FeatureErrorBoundary>
  );
};

// M-F — Mobile intercept for Strategy Builder. The block canvas isn't
// adapted for touch + small screens yet; shipping it half-broken would
// hurt perceived quality more than gating it behind a polished notice.
// The rest of the app stays usable so monitoring/review on phones works.
//
// S59 (2026-06-06) — Per-browser escape hatch. The default mobile
// experience stays unchanged for all visitors, but the gate now
// surfaces an "Open canvas anyway" link that flips a localStorage
// flag. That flag is read in the renderContent('builder') branch and
// in this notice, so once it's set the canvas renders on mobile.
// Stored per-browser (not synced to the user record) on purpose:
// it's a local UX preference, not a global setting. Clearing browser
// data resets to the default gated behaviour.
//
// S60 (2026-06-06) — read/write moved to src/hooks/useBuilderMobileOverride.ts
// so the StrategyBuilderPage (which now restructures its layout for
// the mobile shell) can subscribe to the same flag without each
// consumer reimplementing the localStorage dance.

interface BuilderDesktopOnlyNoticeProps {
  onPick: (tab: string) => void;
  // S59 — parent passes a setter so toggling the override re-renders
  // the App's gate decision without a full page reload.
  onOverrideCanvas: () => void;
}

const BuilderDesktopOnlyNotice: React.FC<BuilderDesktopOnlyNoticeProps> = ({ onPick, onOverrideCanvas }) => (
  <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
    <div className="max-w-md mx-auto text-center mt-8">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-tr from-indigo-500 via-blue-500 to-cyan-500 flex items-center justify-center shadow-md">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7 text-white">
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-slate-900 mb-2">Strategy Builder is desktop-only.</h2>
      <p className="text-sm text-slate-600 mb-6">
        The block canvas needs a wider screen + a mouse. Pick this back
        up on a laptop. In the meantime, the read-only surfaces below
        work fine on this device.
      </p>
      <div className="grid grid-cols-1 gap-2 text-left">
        <button
          onClick={() => onPick('backtest')}
          className="p-3 rounded-md border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50"
        >
          <div className="text-sm font-semibold text-slate-900">Backtest &amp; Validate</div>
          <div className="text-xs text-slate-500 mt-0.5">View walk-forward results + saved variants.</div>
        </button>
        <button
          onClick={() => onPick('portfolios')}
          className="p-3 rounded-md border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50"
        >
          <div className="text-sm font-semibold text-slate-900">Portfolios</div>
          <div className="text-xs text-slate-500 mt-0.5">Review members, weights, correlation, and last evaluation.</div>
        </button>
        <button
          onClick={() => onPick('signals')}
          className="p-3 rounded-md border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50"
        >
          <div className="text-sm font-semibold text-slate-900">Signals</div>
          <div className="text-xs text-slate-500 mt-0.5">Recent alerts fired by your deployments.</div>
        </button>
        <button
          onClick={() => onPick('settings')}
          className="p-3 rounded-md border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50"
        >
          <div className="text-sm font-semibold text-slate-900">Settings</div>
          <div className="text-xs text-slate-500 mt-0.5">Manage webhook secret, email, and tier.</div>
        </button>
      </div>
      {/* S59 — Escape hatch. Stored per-browser, doesn't affect other
          visitors. Worded so it's clear this is opt-in-cramped, not
          a hidden "secret" path. */}
      <button
        type="button"
        onClick={onOverrideCanvas}
        className="mt-6 text-xs text-slate-500 underline hover:text-slate-700"
      >
        Open canvas anyway (it'll be cramped — best on a laptop)
      </button>
    </div>
  </div>
);

// S59 — Inline banner shown above the Builder when the user is on
// mobile but has opted in via the localStorage flag. Surfaces the
// revert path so it's not a dead-end: tapping it restores the
// gate. Without this the only way back would be clearing browser
// storage.
const BuilderMobileOverrideBanner: React.FC<{ onRevert: () => void }> = ({ onRevert }) => (
  <div className="bg-amber-50 border-b border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-center justify-between gap-2">
    <span>You enabled the canvas on mobile. It's not optimised — drag and multi-pane editing are awkward on touch.</span>
    <button
      type="button"
      onClick={onRevert}
      className="shrink-0 px-2 py-0.5 bg-white border border-amber-300 rounded text-amber-900 hover:bg-amber-100 whitespace-nowrap"
    >
      Switch back
    </button>
  </div>
);

// Tabs that map 1:1 to a URL hash (#builder, #convert, …). Matches the
// renderContent() switch cases; anything else falls back to the default tab.
const ROUTABLE_TABS = new Set([
  'builder', 'backtest', 'optimize', 'portfolios', 'settings', 'replay', 'deploy', 'signals', 'convert',
  // S16 (2026-06-04) — top-bar Dashboard + Community destinations.
  'dashboard', 'community',
  // Discovery (Phase 3A) — routable so the workspace is deep-linkable + reload-stable
  // when the flag is on; the renderContent case re-checks the gate (off → no UI).
  'discovery',
]);
const DEFAULT_TAB = 'builder';

// Always-on pre-auth hash routes (rendered before the auth gate). Used to scroll to top on
// same-document navigation into one of them; the flag-gated demo routes are excluded on
// purpose (scrolling is harmless but they're not linked from the landing page).
const PRE_AUTH_ROUTES = new Set(['#evidence', '#evidence/history', '#showcase']);

/** Current URL hash → a routable tab id, or null if missing/invalid. */
function tabFromHash(): string | null {
  const h = (window.location.hash || '').replace(/^#/, '').trim();
  return ROUTABLE_TABS.has(h) ? h : null;
}

function App() {
  // Initialize from the URL hash so a deep-link / refresh restores the page;
  // falls back to the default tab when the hash is missing or invalid.
  const [activeTab, setActiveTab] = useState<string>(() => tabFromHash() ?? DEFAULT_TAB);
  const { user, loading: authLoading } = useAuth();

  // ── URL hash ↔ activeTab sync (lightweight, no router) ──
  // Reflects the active tab in the URL (#convert, #backtest, …), restores it on
  // load, and supports browser Back/Forward — without react-router. Gated to
  // the signed-in app shell so it never fights the landing page's own
  // #workflow anchor (and neutralizes a leftover #workflow on first app render).
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const didInitHashSync = useRef(false);

  // Load the server-persisted admin flag (fundamentals dimension). RE-READ whenever
  // the authenticated user changes — the RLS read requires an authenticated session,
  // so a mount-only read can race auth restore and fail-closed to OFF. Reading on
  // `user` guarantees an authenticated read (and picks up an admin's toggle on next
  // login/refresh). Fail-closed if unread; the env kill switch stays supreme.
  useEffect(() => { ensureFundamentalsFlag(); if (user) void loadFundamentalsFlag(); }, [user]);

  // Write: keep the URL hash in step with the active tab.
  useEffect(() => {
    if (!user) return;
    // NEVER clobber a pre-auth route: a signed-in user opening /#evidence must stay on
    // the evidence page, not get bounced to the default tab (bug: '#evidence' is not a
    // ROUTABLE_TAB, so activeTab falls back to 'builder' and this effect rewrote the URL).
    if (PRE_AUTH_ROUTES.has(window.location.hash)) { didInitHashSync.current = true; return; }
    const want = '#' + activeTab;
    if (window.location.hash === want) { didInitHashSync.current = true; return; }
    if (didInitHashSync.current) {
      window.history.pushState(null, '', want);    // user navigation → adds a Back entry
    } else {
      window.history.replaceState(null, '', want); // first app render → no extra entry; clears leftover #workflow
      didInitHashSync.current = true;
    }
  }, [activeTab, user]);

  // Pre-auth hash routes (#evidence, …) are matched during render by
  // reading window.location.hash directly. A same-document hash click (e.g. the landing
  // page's /#evidence links) doesn't reload the page, and the tab-sync listener
  // below is gated to signed-in users — so without this, signed-out visitors' clicks only
  // took effect after a manual refresh. Bump a counter to re-render on every hashchange,
  // and scroll to top when landing on a pre-auth route (there's no reload to do it for us).
  const [, setHashTick] = useState(0);
  useEffect(() => {
    const onHash = () => {
      setHashTick((t) => t + 1);
      if (PRE_AUTH_ROUTES.has(window.location.hash)) window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Read: respond to Back/Forward (popstate) and manual hash edits (hashchange).
  useEffect(() => {
    if (!user) return;
    const onNav = () => {
      const t = tabFromHash();
      if (t && t !== activeTabRef.current) setActiveTab(t);
    };
    window.addEventListener('popstate', onNav);
    window.addEventListener('hashchange', onNav);
    return () => {
      window.removeEventListener('popstate', onNav);
      window.removeEventListener('hashchange', onNav);
    };
  }, [user]);

  // M-F — mobile nav drawer state. The desktop sidebar (md+) is always
  // visible via Tailwind responsive classes; below md we show a drawer
  // that toggles via the Header hamburger.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isMobile = useIsMobile();
  // S59/S60 — per-browser opt-in to render the Builder canvas on
  // mobile. The hook keeps the flag synced across components in the
  // same tab (via a synthetic storage event) so StrategyBuilderPage's
  // mobile-shell layout switches in lockstep with this gate decision.
  const [builderMobileOverride, setBuilderMobileOverride] = useBuilderMobileOverride();
  const enableBuilderMobileOverride  = () => setBuilderMobileOverride(true);
  const disableBuilderMobileOverride = () => setBuilderMobileOverride(false);

  // M-F — lock body scroll while the mobile drawer is open so the
  // underlying page can't scroll behind it (the iOS / Android default
  // otherwise feels broken).
  useEffect(() => {
    if (mobileNavOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [mobileNavOpen]);

  // M-F — auto-close the drawer when the viewport grows past the mobile
  // breakpoint (e.g. user rotates a tablet from portrait to landscape).
  useEffect(() => {
    if (!isMobile && mobileNavOpen) setMobileNavOpen(false);
  }, [isMobile, mobileNavOpen]);

  // M-D — first-run onboarding state. Optimistically reads localStorage
  // so we don't flash the modal on every page load, then refreshes from
  // user_preferences in the background. `null` = "still resolving";
  // we hide the tour until we have a definitive answer to avoid a flash.
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  useEffect(() => {
    if (!user) {
      setOnboardingCompleted(null);
      return;
    }
    // Optimistic local hint first.
    import('./services/onboarding').then(({ readLocalOnboardingHint, getOnboardingState }) => {
      setOnboardingCompleted(readLocalOnboardingHint());
      // Authoritative DB check.
      getOnboardingState().then(s => setOnboardingCompleted(s.completed)).catch(() => {});
    });
  }, [user]);

  // Initialize error handling
  useEffect(() => {
    // Initialize global error handling
    errorReporter.init();
    enableErrorDebugging();

    // Add performance monitoring
    if ('performance' in window && 'measure' in window.performance) {
      window.performance.mark('app-start');
    }

    // Hot Module Replacement handling (for Vite)
    if (import.meta.hot) {
      import.meta.hot.on('vite:error', (error) => {
        console.error('🔥 HMR Error:', error);
      });
    }

    logger.debug('🛡️ App error handling initialized');

    return () => {
      if ('performance' in window && 'measure' in window.performance) {
        window.performance.mark('app-end');
        window.performance.measure('app-duration', 'app-start', 'app-end');
      }
    };
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case 'builder':
        // M-F — mobile intercept. The block canvas needs drag-drop +
        // multi-pane editing that doesn't survive a 6-inch screen. Instead
        // of shipping a half-broken Builder, show a friendly desktop-only
        // notice and steer the user toward the read surfaces that DO work.
        //
        // S59 — `builderMobileOverride` is the per-browser opt-in flag.
        // When true (user tapped "Open canvas anyway" earlier), we skip
        // the gate even on mobile and render the canvas with a small
        // revert banner on top so the path back isn't a dead-end.
        if (isMobile && !builderMobileOverride) {
          return (
            <PageWrapper pageName="Strategy Builder">
              <BuilderDesktopOnlyNotice
                onPick={setActiveTab}
                onOverrideCanvas={enableBuilderMobileOverride}
              />
            </PageWrapper>
          );
        }
        return (
          <PageWrapper pageName="Strategy Builder">
            <div className="flex-1 flex flex-col min-h-0">
              {isMobile && builderMobileOverride && (
                <BuilderMobileOverrideBanner onRevert={disableBuilderMobileOverride} />
              )}
              <div className="flex-1 min-h-0">
                <StrategyBuilderPage />
              </div>
            </div>
          </PageWrapper>
        );
      case 'backtest':
        return (
          <PageWrapper pageName="Backtest">
            <Suspense fallback={<PageLoading />}>
              <BacktestPage />
            </Suspense>
          </PageWrapper>
        );
      case 'optimize':
        return (
          <PageWrapper pageName="Adaptive AI Optimization">
            <Suspense fallback={<PageLoading />}>
              <OptimizationPanel />
            </Suspense>
          </PageWrapper>
        );
      case 'portfolios':
        return (
          <PageWrapper pageName="Portfolios">
            <Suspense fallback={<PageLoading />}>
              <PortfoliosPage />
            </Suspense>
          </PageWrapper>
        );
      case 'settings':
        return (
          <PageWrapper pageName="Settings">
            <Suspense fallback={<PageLoading />}>
              <SettingsPage />
            </Suspense>
          </PageWrapper>
        );
      case 'replay':
        return (
          <PageWrapper pageName="Replay">
            <Suspense fallback={<PageLoading />}>
              <ReplayPage />
            </Suspense>
          </PageWrapper>
        );
      case 'deploy':
        return (
          <PageWrapper pageName="Deploy">
            <Suspense fallback={<PageLoading />}>
              <DeployPage />
            </Suspense>
          </PageWrapper>
        );
      case 'signals':
        return (
          <PageWrapper pageName="Signals">
            <Suspense fallback={<PageLoading />}>
              <SignalsPage />
            </Suspense>
          </PageWrapper>
        );
      case 'convert':
        return (
          <PageWrapper pageName="Convert">
            <Suspense fallback={<PageLoading />}>
              <ConvertPage />
            </Suspense>
          </PageWrapper>
        );
      case 'dashboard':
        // S16 (2026-06-04) — Admin dashboard. Currently the access-gate
        // shell only; S17-S19 plug in the DB migration + service layer +
        // real management UI.
        return (
          <PageWrapper pageName="Admin Dashboard">
            <Suspense fallback={<PageLoading />}>
              <AdminPage />
            </Suspense>
          </PageWrapper>
        );
      case 'community':
        // S16 (2026-06-04) — Community. Currently a notice; S20 wires
        // the public-template feed.
        return (
          <PageWrapper pageName="Community">
            <Suspense fallback={<PageLoading />}>
              <CommunityPage />
            </Suspense>
          </PageWrapper>
        );
      case 'discovery':
        // Discovery (Phase 3A/3B) — gated by the global flag AND the pilot-cohort
        // gate (fail-closed). When access is denied the check short-circuits BEFORE
        // referencing DiscoveryPage, so neither the workspace nor its lazy chunk is
        // reachable for a disabled / killed / non-cohort user.
        if (!resolveDiscoveryAccess().allowed) {
          return (
            <PageWrapper pageName="Feature">
              <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-gray-50">
                <p className="text-sm text-gray-500">This feature is not available.</p>
              </div>
            </PageWrapper>
          );
        }
        return (
          <PageWrapper pageName="Discovery">
            <Suspense fallback={<PageLoading />}>
              <DiscoveryPage />
            </Suspense>
          </PageWrapper>
        );
      // case 'data':
      //   return (
      //     <PageWrapper pageName="Market Data">
      //       <MarketDataPage />
      //     </PageWrapper>
      //   );
      default:
        return (
          <PageWrapper pageName="Feature">
            <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-gray-50">
              <div className="bg-white p-8 rounded-lg shadow-md max-w-md">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Coming Soon
                </h2>
                <p className="text-gray-600">
                  This feature is currently under development and will be
                  available in a future update.
                </p>
              </div>
            </div>
          </PageWrapper>
        );
    }
  };

  // Validation Playground — rendered BEFORE the auth gate, and only when its
  // independent flag is on. A no-auth, no-DB, no-backtest review surface; inert
  // (this branch never taken) for everyone by default.
  if (
    typeof window !== 'undefined' &&
    window.location.hash === '#validation-playground' &&
    validationPlaygroundGate().enabled
  ) {
    return (
      <GlobalErrorBoundary>
        <Suspense fallback={<AuthLoading />}>
          <ValidationPlayground />
        </Suspense>
      </GlobalErrorBoundary>
    );
  }

  // Reality Check demo — pre-auth, flag-gated. Seeded, no-DB investor/customer walkthrough
  // that runs the real engine chain + real report section on bundled candles.
  if (
    typeof window !== 'undefined' &&
    window.location.hash === '#reality-check-demo' &&
    realityCheckDemoGate().enabled
  ) {
    return (
      <GlobalErrorBoundary>
        <Suspense fallback={<AuthLoading />}>
          <RealityCheckDemoPage />
        </Suspense>
      </GlobalErrorBoundary>
    );
  }

  // Pine → Reality Check demo — pre-auth acquisition funnel, behind the EXISTING Pine importer
  // gate + remote fleet kill (fail-closed while the remote config is unread).
  if (typeof window !== 'undefined' && window.location.hash === '#pine-demo') {
    ensurePineRemoteConfig();
    if (pineImporterGate({ remote: getPineRemoteKillState() }).enabled) {
      return (
        <GlobalErrorBoundary>
          <Suspense fallback={<AuthLoading />}>
            <PineDemoPage />
          </Suspense>
        </GlobalErrorBoundary>
      );
    }
  }

  // Hypothesis Engine playground — same pre-auth, flag-gated pattern.
  if (
    typeof window !== 'undefined' &&
    window.location.hash === '#hypothesis-playground' &&
    hypothesisPlaygroundGate().enabled
  ) {
    return (
      <GlobalErrorBoundary>
        <Suspense fallback={<AuthLoading />}>
          <HypothesisPlayground />
        </Suspense>
      </GlobalErrorBoundary>
    );
  }

  // Customer assessment status — pre-auth, keyed on an unguessable ?assessment=<token>.
  // No account needed; the token IS the auth. The page fetches only safe allowlisted fields
  // via the assessment-status edge fn (never PII/notes/price/strategy).
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('assessment')) {
    return (
      <GlobalErrorBoundary>
        <Suspense fallback={<AuthLoading />}>
          <AssessmentStatusPage />
        </Suspense>
      </GlobalErrorBoundary>
    );
  }

  // Shared Reality Check report — pre-auth, keyed on an unguessable ?report=<token>.
  // The token maps (server-side) to a saved report; the page renders the deterministic,
  // print-to-PDF HTML. Safe to send to a customer; no raw licensed data (derived verdicts only).
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('report')) {
    return (
      <GlobalErrorBoundary>
        <Suspense fallback={<AuthLoading />}>
          <SharedReportPage />
        </Suspense>
      </GlobalErrorBoundary>
    );
  }


  // Evidence — pre-auth: the 20% Evidence Ledger + validated-class claims only (P9 hard
  // separation). Renders fail-closed generated ledger data; no gate/scoring logic.
  // Research archive — the complete prior record (P10-P15 + earlier Epoch-2), preserved
  // unchanged; the active product story lives at #evidence.
  if (typeof window !== 'undefined' && window.location.hash === '#evidence/history') {
    return (
      <GlobalErrorBoundary>
        <Suspense fallback={<AuthLoading />}>
          <EvidenceArchivePage />
        </Suspense>
      </GlobalErrorBoundary>
    );
  }

  if (typeof window !== 'undefined' && window.location.hash === '#evidence') {
    return (
      <GlobalErrorBoundary>
        <Suspense fallback={<AuthLoading />}>
          <EvidencePage />
        </Suspense>
      </GlobalErrorBoundary>
    );
  }

  // Optimizer Showcase — pre-auth: exploratory demonstration runs (in-sample/unvalidated,
  // no forward-return expectation). Hard-separated from #evidence; never merged.
  if (typeof window !== 'undefined' && window.location.hash === '#showcase') {
    return (
      <GlobalErrorBoundary>
        <Suspense fallback={<AuthLoading />}>
          <ShowcasePage />
        </Suspense>
      </GlobalErrorBoundary>
    );
  }

  // Reality Check v1-vs-v2 comparison — pre-auth, in-app, experimental. Renders from bundled data.
  // v2 is R&D only; v1 verdicts/labels/reports are untouched.

  // Block the whole app until auth resolves and the user is signed in.
  // This single gate makes the app accessible only to registered users —
  // there's no anonymous read path past this point.
  if (authLoading) {
    return (
      <GlobalErrorBoundary>
        <AuthLoading />
      </GlobalErrorBoundary>
    );
  }
  if (!user) {
    return (
      <GlobalErrorBoundary>
        <Suspense fallback={<AuthLoading />}>
          <LandingPage />
        </Suspense>
      </GlobalErrorBoundary>
    );
  }

  return (
    <GlobalErrorBoundary>
      <div className="flex flex-col h-screen">
        {/* Header with error boundary */}
        <FeatureErrorBoundary
          featureName="Header"
          fallback={
            <div className="h-16 bg-red-50 border-b border-red-200 flex items-center justify-center">
              <span className="text-red-600 text-sm">
                Header failed to load
              </span>
            </div>
          }
        >
          <Header onMobileMenuToggle={() => setMobileNavOpen(true)} />
        </FeatureErrorBoundary>

        {/* M-F — mobile drawer. Same Sidebar component, rendered with
            mobileOpen so it animates in/out via translate. Lives outside
            the flex row so its fixed positioning doesn't disturb layout. */}
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onboardingCompleted={onboardingCompleted === true}
          mobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}
        />

        <div className="flex flex-1 overflow-hidden">
          {/* Desktop sidebar with error boundary. Hidden on mobile;
              the drawer above takes over there. */}
          <FeatureErrorBoundary
            featureName="Sidebar"
            fallback={
              <div className="w-64 bg-red-50 border-r border-red-200 flex items-center justify-center">
                <div className="text-center p-4">
                  <svg
                    className="w-8 h-8 text-red-500 mx-auto mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01"
                    />
                  </svg>
                  <span className="text-red-600 text-sm">Sidebar Error</span>
                </div>
              </div>
            }
          >
            <div className="hidden md:block">
              <Sidebar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                // M-D — progressive disclosure. Advanced surfaces are hidden
                // until the user finishes (or skips) onboarding. `null`
                // (auth-resolving) is treated as "not completed" so we don't
                // briefly flash the full sidebar before hiding it.
                onboardingCompleted={onboardingCompleted === true}
              />
            </div>
          </FeatureErrorBoundary>

          {/* Main content with error boundary.
              Must be a flex container so pages that use `flex-1 overflow-y-auto`
              for their scroll region (Settings, Signals, Deploy, Optimize, etc.)
              receive a defined height. Without `flex flex-col`, those pages
              collapse to zero height and scrolling appears broken. */}
          <main className="flex-1 overflow-hidden flex flex-col">
            <FeatureErrorBoundary featureName="Main Content">
              {renderContent()}
            </FeatureErrorBoundary>
          </main>
        </div>

        {/* Development indicators */}
        {process.env.NODE_ENV === 'development' && (
          <div className="fixed bottom-4 left-4 z-40">
            <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded shadow-lg">
              DEV • Error Handling Active
            </div>
          </div>
        )}
      </div>

      {/* M-D — first-run onboarding. Renders only when we know for sure
          the user hasn't completed it. Lazy-loaded so its ~12 KB doesn't
          bloat the returning-user bundle. */}
      {onboardingCompleted === false && (
        <Suspense fallback={null}>
          <OnboardingFlow
            onComplete={() => setOnboardingCompleted(true)}
            onNavigateAfter={(tab) => setActiveTab(tab)}
          />
        </Suspense>
      )}

      {/* Feedback widget lives in the header now (Layout/Header.tsx)
          so it sits in the top-right next to Sign Out and doesn't
          float over panel Save buttons. The pageContext is no longer
          plumbed through — the header can't see activeTab anyway and
          we can re-add it if/when we need per-tab attribution. */}

      {/* Error overlay for development */}
      <ErrorOverlay />
    </GlobalErrorBoundary>
  );
}

export default App;
